import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Isolated so the Google GSI code-client (which requires client_id) only ever
// mounts when a client_id exists — a missing env var can never crash the login page.
function GoogleContinueButton({ analysisId, redirectTo, onBusy }) {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [blocked, setBlocked] = useState(false);
  const googleLogin = useGoogleLogin({
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      onBusy(true);
      setBlocked(false);
      try {
        const { data } = await api.post("/auth/google/code", { code, analysis_id: analysisId });
        loginWithToken(data.token, data.user);
        toast.success("Signed in with Google");
        navigate(redirectTo);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Google sign-in failed");
        onBusy(false);
      }
    },
    onError: () => {
      setBlocked(true);
      toast.error("Google didn't complete the sign-in. See the note below the button.");
    },
  });
  return (
    <>
      <Button data-testid="auth-google" variant="outline" onClick={() => googleLogin()} className="h-12 w-full rounded-none border-black/20">
        Continue with Google
      </Button>
      {blocked ? null : (
        <button type="button" data-testid="google-auth-help-link" onClick={() => setBlocked(true)}
          className="mt-2 w-full text-center text-xs text-slate-400 underline hover:text-klein">
          Google sign-in not working?
        </button>
      )}
      {blocked && (
        <div className="mt-3 border border-warning/40 bg-warning/5 p-3 text-xs text-slate-700" data-testid="google-auth-help">
          <b>Google blocked the popup (Error 400: origin_mismatch).</b> This origin isn't registered on your
          Google Cloud OAuth client yet. In <span className="font-medium">Google Cloud Console → APIs &amp; Services →
          Credentials → your OAuth 2.0 Web client</span>, add this exact value under
          <span className="font-medium"> Authorized JavaScript origins</span>, then save and retry (changes can take a few minutes):
          <button type="button" data-testid="copy-origin-btn"
            onClick={() => { navigator.clipboard?.writeText(window.location.origin); toast.success("Origin copied"); }}
            className="mt-2 block w-full break-all border border-black/15 bg-white px-2 py-1.5 text-left font-mono-data text-[11px] hover:border-klein">
            {window.location.origin} <span className="text-klein">— click to copy</span>
          </button>
          <span className="mt-2 block text-slate-500">Email/password sign-in works regardless.</span>
        </div>
      )}
    </>
  );
}

export default function Auth({ mode = "login" }) {
  useSeo({
    title: mode === "register" ? "Create your A.R.I.A. account" : "Sign in to A.R.I.A.",
    description: mode === "register"
      ? "Create an A.R.I.A. account to save your competitive analyses, unlock full reports and run your 30-day growth plan."
      : "Sign in to A.R.I.A. to view your competitive analyses, reports and growth plan.",
    path: mode === "register" ? "/register" : "/login",
    noindex: true,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const isRegister = mode === "register";
  const analysisId = location.state?.analysisId || JSON.parse(localStorage.getItem("oa_purchase_intent") || "null")?.analysis_id;
  const redirectTo = location.state?.redirectTo || "/dashboard";

  const submit = async () => {
    setBusy(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister
        ? { name: form.name, email: form.email, password: form.password, analysis_id: analysisId }
        : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      loginWithToken(data.token, data.user);
      toast.success(isRegister ? "Account created" : "Welcome back");
      navigate(redirectTo);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Authentication failed");
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden bg-brand-dark lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:p-12">
        <Link to="/" className="flex items-center gap-2 text-white">
          <span className="flex h-9 w-9 overflow-hidden rounded-lg bg-black ring-1 ring-black/10"><img src="/aria-mark.png" alt="A.R.I.A." className="h-full w-full object-cover" /></span>
          <span className="font-display text-lg font-semibold">A.R.I.A.</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-light leading-tight text-white">
            Turn your market into a prioritized growth plan.
          </h2>
          <p className="mt-4 text-slate-400">AI-powered competitive intelligence for local businesses.</p>
        </div>
        <span className="mono-label !text-slate-600">AI diagnostics, not financial advice</span>
      </div>

      <div className="flex w-full items-center justify-center px-5 py-16 lg:w-1/2">
        <div className="w-full max-w-sm">
          <span className="mono-label">{isRegister ? "Create account" : "Welcome back"}</span>
          <h1 className="mt-2 font-display text-3xl tracking-tight">{isRegister ? "Save your results" : "Sign in"}</h1>

          <div className="mt-8 space-y-4">
            {isRegister && (
              <div>
                <Label className="mono-label">Name</Label>
                <Input data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-12 rounded-none border-black/20 focus-visible:ring-klein" />
              </div>
            )}
            <div>
              <Label className="mono-label">Email</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-12 rounded-none border-black/20 focus-visible:ring-klein" />
            </div>
            <div>
              <Label className="mono-label">Password</Label>
              <div className="relative">
                <Input data-testid="auth-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} className="mt-2 h-12 rounded-none border-black/20 pr-12 focus-visible:ring-klein" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 top-2 flex h-12 w-12 items-center justify-center text-slate-400 transition-colors hover:text-klein">
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <Button data-testid="auth-submit" disabled={busy} onClick={submit} className="h-12 w-full rounded-none bg-brand-dark text-white hover:bg-klein">
              {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
            </Button>
          </div>

          <div className="my-6 flex items-center gap-3"><span className="h-px flex-1 bg-black/10" /><span className="mono-label">or</span><span className="h-px flex-1 bg-black/10" /></div>

          {GOOGLE_CLIENT_ID
            ? <GoogleContinueButton analysisId={analysisId} redirectTo={redirectTo} onBusy={setBusy} />
            : <Button data-testid="auth-google" variant="outline" disabled className="h-12 w-full rounded-none border-black/20 opacity-60">Continue with Google (unavailable)</Button>}

          <p className="mt-6 text-center text-sm text-slate-600">
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="text-klein underline">Sign in</Link></>
            ) : (
              <>New here? <Link to="/register" className="text-klein underline">Create an account</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
